import { createFileRoute, useParams } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { dataProvider as supabase } from "@/lib/providers/data-provider";
import { guardRoute } from "@/lib/permissions";
import { loadPlatformPrintDocument, type PlatformPrintDoc } from "@/lib/platform-invoice-adapter";
import { brandingToPdfInputAsync, loadPlatformBrandingSettings } from "@/lib/platform-branding";
import {
  downloadPlatformBillingPdf,
  printPlatformBillingPdf,
} from "@/lib/platform-billing-pdf";
import { PrintToolbar } from "@/components/print/PrintToolbar";
import { Loader2 } from "lucide-react";

export const Route = createFileRoute("/platform/billing-print/$id")({
  beforeLoad: ({ location }) => guardRoute(location.pathname),
  head: () => ({ meta: [{ title: "Billing Document · AVS Gold ERP Platform" }] }),
  component: BillingPrintPage,
});

type Firm = { id: string; name: string; gstin: string | null; address: string | null };

const DOC_TYPE_LABEL: Record<string, string> = {
  quotation: "QUOTATION",
  proforma: "PROFORMA INVOICE",
  tax_invoice: "TAX INVOICE",
  renewal_invoice: "RENEWAL INVOICE",
  credit_note: "CREDIT NOTE",
  payment_receipt: "PAYMENT RECEIPT",
};

function rs(minor: number | null | undefined): string {
  return `₹${((minor ?? 0) / 100).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function BillingPrintPage() {
  const { id } = useParams({ from: "/platform/billing-print/$id" });
  const [doc, setDoc] = useState<PlatformPrintDoc | null>(null);
  const [firm, setFirm] = useState<Firm | null>(null);
  const [branding, setBranding] = useState<Awaited<
    ReturnType<typeof loadPlatformBrandingSettings>
  > | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      const { doc: loaded, error: loadError } = await loadPlatformPrintDocument(id);
      if (loadError || !loaded) {
        setError(loadError ?? "Document not found.");
        setLoading(false);
        return;
      }
      setDoc(loaded);
      const [{ data: firmRow }, brandSettings] = await Promise.all([
        supabase
          .from("organizations" as never)
          .select("id,name,gstin,address")
          .eq("id", loaded.firm_id)
          .maybeSingle(),
        loadPlatformBrandingSettings(),
      ]);
      setFirm((firmRow as Firm | null) ?? null);
      setBranding(brandSettings);
      setLoading(false);
    })();
  }, [id]);

  const pdfInput = useMemo(() => {
    if (!doc || !branding) return null;
    return {
      doc,
      buyerName: firm?.name ?? doc.firm_id,
      buyerAddress: firm?.address,
      buyerGstin: firm?.gstin,
      brandingPromise: brandingToPdfInputAsync(branding),
    };
  }, [doc, firm, branding]);

  if (loading) {
    return (
      <div className="grid min-h-[50vh] place-items-center bg-white">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }
  if (error || !doc || !branding) {
    return (
      <div className="mx-auto max-w-xl p-8 text-center text-sm text-muted-foreground bg-white">
        {error ?? "Document not found."}
      </div>
    );
  }

  const isInterState = (doc.igst_minor ?? 0) > 0;
  const gstRate = doc.data?.gst_rate_percent ?? 0;
  const logo = branding.logoDocument || branding.logoPrimary || branding.logoCompact;
  const companyName = branding.legalName || branding.appName;
  const docTitle = DOC_TYPE_LABEL[doc.document_type] ?? doc.document_type;

  async function buildPdfInputResolved() {
    if (!pdfInput) return null;
    const brandingPdf = await pdfInput.brandingPromise;
    return {
      doc: pdfInput.doc,
      buyerName: pdfInput.buyerName,
      buyerAddress: pdfInput.buyerAddress,
      buyerGstin: pdfInput.buyerGstin,
      branding: brandingPdf,
    };
  }

  const handlePrint = async () => {
    const resolved = await buildPdfInputResolved();
    if (!resolved) return;
    printPlatformBillingPdf(resolved);
  };

  const handleDownloadPdf = async () => {
    const resolved = await buildPdfInputResolved();
    if (!resolved) return;
    setDownloadingPdf(true);
    try {
      await downloadPlatformBillingPdf(resolved);
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PrintToolbar
        title={docTitle}
        docNumber={doc.document_no}
        onPrint={handlePrint}
        onDownloadPdf={handleDownloadPdf}
        downloadingPdf={downloadingPdf}
        documentSize="a4"
        backUrl="/platform?view=billing"
      />

      <div className="flex-1 p-4 md:p-8 flex justify-center items-start overflow-x-auto overflow-y-auto">
        <div
          data-testid="print-layout-root"
          data-print-size="a4"
          className="bg-white text-slate-900 shadow-md print:shadow-none border border-slate-200 print:border-none rounded-sm font-sans"
          style={{ width: "210mm", minHeight: "297mm", padding: "14mm 16mm", boxSizing: "border-box" }}
        >
          {/* Header Block with AVS Branding & Logo */}
          <div className="flex items-start justify-between border-b-2 border-amber-600/30 pb-5">
            <div className="flex items-start gap-4">
              <img
                src={logo || "/assets/logo.png"}
                alt="AVS Logo"
                className="h-16 w-auto max-w-[120px] object-contain rounded-md p-1 border border-slate-100 bg-white"
                onError={(e) => {
                  // fallback to standard logo if custom fails
                  (e.target as HTMLElement).style.display = "none";
                }}
              />
              <div className="space-y-0.5">
                <div className="flex items-center gap-2">
                  <h1 className="text-base font-bold tracking-tight text-slate-950 font-serif">
                    {branding.legalName || "Arivahly Venture Sphere Private Limited"}
                  </h1>
                </div>
                <p className="text-[11px] font-medium text-amber-700 font-serif">
                  {branding.appName || "AVS Gold ERP Platform"} · {branding.tagline || "Enterprise Jewellery Cloud Suite"}
                </p>
                <p className="text-[10px] text-slate-600 max-w-sm leading-relaxed mt-1">
                  {branding.address || "AVS Towers, Tech Hub, Ichalkaranji - 416115, Maharashtra, India"}
                </p>
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-slate-600 pt-0.5 font-mono">
                  {branding.gstin && <span><strong>GSTIN:</strong> {branding.gstin}</span>}
                  {branding.cin && <span><strong>CIN:</strong> {branding.cin}</span>}
                  <span><strong>Email:</strong> {branding.email || "billing@arivahly.in"}</span>
                </div>
              </div>
            </div>

            <div className="text-right space-y-1">
              <div className="inline-block bg-slate-900 text-amber-400 px-3 py-1 rounded text-xs font-serif font-bold uppercase tracking-wider">
                {docTitle}
              </div>
              <div className="text-[11px] font-mono mt-2 space-y-0.5">
                <p className="text-slate-900 font-semibold">
                  Invoice No: <span className="text-amber-800">{doc.document_no}</span>
                </p>
                {doc.issued_at && (
                  <p className="text-slate-600">
                    Date: {new Date(doc.issued_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                  </p>
                )}
                {doc.due_at && (
                  <p className="text-slate-600">
                    Due: {new Date(doc.due_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                  </p>
                )}
                <p className="text-slate-600">Place of Supply: 27 - Maharashtra</p>
              </div>
              <div className="pt-1">
                <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-semibold tracking-wide uppercase ${
                  doc.status === "paid" || doc.status === "active"
                    ? "bg-emerald-50 text-emerald-700 border border-emerald-300"
                    : "bg-amber-50 text-amber-800 border border-amber-300"
                }`}>
                  ● {doc.status || "PAID"}
                </span>
              </div>
            </div>
          </div>

          {/* Bill To & Billing Context */}
          <div className="grid grid-cols-2 gap-6 my-5 p-3.5 rounded-lg bg-slate-50 border border-slate-200/80">
            <div>
              <p className="text-[9px] uppercase tracking-wider text-slate-500 font-bold font-mono">
                BILLED TO / CUSTOMER DETAILS
              </p>
              <h3 className="text-xs font-bold text-slate-900 mt-1 font-serif">
                {firm?.name ?? doc.firm_id}
              </h3>
              {firm?.address ? (
                <p className="text-[10px] whitespace-pre-line text-slate-600 mt-0.5 leading-normal">
                  {firm.address}
                </p>
              ) : (
                <p className="text-[10px] text-slate-500 italic mt-0.5">Registered Client Organization</p>
              )}
              {firm?.gstin && (
                <p className="text-[10px] font-mono text-slate-700 mt-1">
                  <strong>Client GSTIN:</strong> {firm.gstin}
                </p>
              )}
            </div>

            <div className="border-l border-slate-200 pl-4 space-y-1">
              <p className="text-[9px] uppercase tracking-wider text-slate-500 font-bold font-mono">
                SERVICE & REMITTANCE SPECIFICATION
              </p>
              <p className="text-[10px] text-slate-700">
                <strong>Service Nature:</strong> Cloud Software & SaaS Hosting
              </p>
              <p className="text-[10px] text-slate-700 font-mono">
                <strong>SAC Code:</strong> 998313 (Information Technology Services)
              </p>
              <p className="text-[10px] text-slate-700">
                <strong>Payment Channel:</strong> Razorpay Secure Gateway / NetBanking / UPI
              </p>
              {(doc.data as any)?.payment_id && (
                <p className="text-[10px] font-mono text-slate-700">
                  <strong>Ref ID:</strong> {(doc.data as any).payment_id}
                </p>
              )}
            </div>
          </div>

          {/* Line Items Table */}
          <div className="border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-[11px] border-collapse">
              <thead>
                <tr className="bg-slate-100 text-slate-800 font-semibold text-[10px] uppercase font-mono border-b border-slate-200">
                  <th className="py-2 px-3 text-left w-10">#</th>
                  <th className="py-2 px-3 text-left">Description & Service Scope</th>
                  <th className="py-2 px-3 text-center w-24">SAC Code</th>
                  <th className="py-2 px-3 text-center w-16">Qty</th>
                  <th className="py-2 px-3 text-right w-28">Rate (INR)</th>
                  <th className="py-2 px-3 text-right w-28">Taxable Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                <tr>
                  <td className="py-3 px-3 text-slate-500 font-mono text-center">1</td>
                  <td className="py-3 px-3">
                    <p className="font-semibold text-slate-900">
                      {doc.data?.description || DOC_TYPE_LABEL[doc.document_type] || doc.document_type}
                    </p>
                    <p className="text-[10px] text-slate-500 mt-0.5">
                      Enterprise jewellery ERP cloud subscription license, tenant cloud isolation, multi-branch operations & priority updates.
                    </p>
                  </td>
                  <td className="py-3 px-3 text-center font-mono text-slate-600">998313</td>
                  <td className="py-3 px-3 text-center font-mono text-slate-600">1</td>
                  <td className="py-3 px-3 text-right font-mono text-slate-700">{rs(doc.taxable_minor)}</td>
                  <td className="py-3 px-3 text-right font-mono font-semibold text-slate-900">{rs(doc.taxable_minor)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Totals & Tax Calculation Breakdown */}
          <div className="mt-4 flex justify-between items-start">
            <div className="max-w-xs space-y-2 text-[10px] text-slate-600 pt-2">
              <div className="p-2.5 rounded bg-amber-50/60 border border-amber-200/50">
                <p className="font-semibold text-amber-900 font-serif">Official Electronic Receipt</p>
                <p className="mt-0.5 text-[9px] leading-relaxed text-amber-800">
                  This tax invoice confirms receipt of subscription dues for AVS Gold ERP. Retain for statutory GST input tax credit reconciliation.
                </p>
              </div>
            </div>

            <div className="w-72 bg-slate-50 border border-slate-200 rounded-lg p-3.5 space-y-2 text-[11px]">
              <div className="flex justify-between text-slate-600">
                <span>Taxable Subtotal:</span>
                <span className="font-mono font-medium text-slate-900">{rs(doc.taxable_minor)}</span>
              </div>

              {isInterState ? (
                <div className="flex justify-between text-slate-600">
                  <span>Integrated GST (IGST {gstRate}%):</span>
                  <span className="font-mono font-medium text-slate-900">{rs(doc.igst_minor)}</span>
                </div>
              ) : (
                <>
                  <div className="flex justify-between text-slate-600">
                    <span>Central GST (CGST {gstRate / 2}%):</span>
                    <span className="font-mono font-medium text-slate-900">{rs(doc.cgst_minor)}</span>
                  </div>
                  <div className="flex justify-between text-slate-600">
                    <span>State GST (SGST {gstRate / 2}%):</span>
                    <span className="font-mono font-medium text-slate-900">{rs(doc.sgst_minor)}</span>
                  </div>
                </>
              )}

              <div className="flex justify-between border-t-2 border-slate-300 pt-2 text-sm font-bold text-slate-950 font-serif">
                <span>Grand Total (INR):</span>
                <span className="font-mono text-amber-800">{rs(doc.amount_minor)}</span>
              </div>

              <div className="flex justify-between text-[11px] text-slate-600 border-t border-slate-200 pt-1.5">
                <span>Amount Paid:</span>
                <span className="font-mono font-semibold text-emerald-700">{rs(doc.paid_minor || doc.amount_minor)}</span>
              </div>

              <div className="flex justify-between text-[11px] font-semibold text-slate-700">
                <span>Balance Due:</span>
                <span className="font-mono">
                  {rs(Math.max(0, (doc.amount_minor ?? 0) - (doc.paid_minor ?? doc.amount_minor ?? 0)))}
                </span>
              </div>
            </div>
          </div>

          {/* Footer & Terms */}
          <div className="mt-12 pt-4 border-t border-slate-200 text-center space-y-1">
            <p className="text-[10px] text-slate-500 font-mono">
              Digitally generated Tax Invoice · {branding.legalName || "Arivahly Venture Sphere Private Limited"} · No physical signature required
            </p>
            <p className="text-[9px] text-slate-400">
              For billing inquiries or tax invoice re-issuance, email {branding.email || "billing@arivahly.in"} or visit {branding.website || "https://erp.arivahly.in"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
